import { Link } from 'react-router-dom'
import { PLATFORMS, TESTIMONIALS, OFFLINE_CENTRES, TECH_PARIVARTAN, HEAD_OFFICE } from '@/data/siteData'
import { useModal } from '@/hooks/useModal'
import Modal from '@/components/Modal/Modal'
import JourneyStrip from '@/components/JourneyStrip/JourneyStrip'
import PlatformCard from '@/components/PlatformCard/PlatformCard'
import StatsBand from '@/components/StatsBand/StatsBand'
import EcosystemSection from '@/components/EcosystemSection/EcosystemSection'
import CtaSection from '@/components/CtaSection/CtaSection'
import GlimpseSlider from '@/components/GlimpseSlider/GlimpseSlider'
import styles from './HomePage.module.css'

export default function HomePage() {
  const { modal, openModal, closeModal } = useModal()

  return (
    <>
      {/* ── HERO ── */}
      <section className={styles.hero} aria-label="Hero">
        <div className={styles.heroBg} aria-hidden="true" />
        <div className={styles.heroGrid} aria-hidden="true" />

        <div className={styles.badge}>
          <span className={styles.badgeDot} />
          NexusAIWeb · Built by TechIntelliVerse
        </div>

        <h1 className={styles.h1}>
          One Platform.<br />
          <span className="grad">Every Step of Your Journey.</span>
        </h1>

        <p className={styles.sub}>
          NexusAIWeb is an AI-powered platform that unifies learning, mentorship,
          internships, and career placement under a single identity — built for the
          complete journey of every Indian student and professional.
        </p>

        <div className={styles.actions}>
          <a
            href="https://sn.nexusaiweb.com"
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-primary btn-lg"
          >
            Start Learning Free
          </a>
          <Link to="/ecosystem" className="btn btn-ghost btn-lg">
            Explore Platforms
          </Link>
        </div>

        {/* Platform pills */}
        <div className={styles.pills}>
          <p className={styles.pillsLabel}>Part of the NexusAIWeb Ecosystem</p>
          <div className={styles.pillsRow}>
            {[
              { color: '#E8642A', name: 'SkillNexus',  href: '/skillnexus' },
              { color: '#22d3ee', name: 'InternNexus', href: '/internnexus' },
              { color: '#8fa8e8', name: 'PromptNexus', href: '/promptnexus' },
            ].map(({ color, name, href }) => (
              <Link key={name} to={href} className={styles.pill}>
                <span className={styles.pillDot} style={{ background: color }} />
                {name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── JOURNEY STRIP ── */}
      <JourneyStrip />

      {/* ── PLATFORMS ── */}
      <section id="platforms" className={styles.platforms} aria-label="Core Platforms">
        <div className={styles.platformsHeader}>
          <p className="section-label">Core Platforms</p>
          <h2 className="section-title" style={{ maxWidth: 680, margin: '0 auto 16px' }}>
            Three platforms. One seamless experience.
          </h2>
        </div>

        {PLATFORMS.map((p, i) => (
          <PlatformCard
            key={p.id}
            platform={p}
            onOpenModal={openModal}
            reverse={i % 2 !== 0}
          />
        ))}
      </section>

      {/* ── STATS ── */}
      <StatsBand />

      {/* ── OFFLINE CENTRES + TECH PARIVARTAN BIHAR ── */}
      <section className={styles.parivartan} aria-label="Offline Centres &amp; Bharat Skilling">
        <div className={styles.parivartanInner}>
          <p className="section-label">Tech Parivartan Bihar — Tier 2/3/4 Initiative</p>
          <h2 className="section-title" style={{ maxWidth: 720, margin: '0 auto 16px' }}>
            Two physical TechHubs. <span className="grad">India-wide online access.</span>
          </h2>
          <p className={styles.parivartanLead}>
            {TECH_PARIVARTAN.tagline}<br />
            <span style={{ opacity: 0.75 }}>{TECH_PARIVARTAN.taglineEn}</span>
          </p>

          {/* Offline centres */}
          <div className={styles.centresGrid}>
            {OFFLINE_CENTRES.map((c) => (
              <div key={c.name} className={styles.centreCard}
                style={{ '--centre-color': c.color }}>
                <span className={styles.centreTier}>{c.tier}</span>
                <h3 className={styles.centreName}>{c.name}</h3>
                <p className={styles.centreTag}>{c.tag}</p>
                {c.mapUrl ? (
                  <a href={c.mapUrl} target="_blank" rel="noopener noreferrer"
                    className={styles.centreAddr}>
                    📍 {c.address} <span style={{ opacity: 0.7 }}>→</span>
                  </a>
                ) : (
                  <p className={styles.centreAddr}>📍 {c.address}</p>
                )}
                <a href={`tel:${c.phone.replace(/[^0-9+]/g, '')}`}
                  className={styles.centrePhone}>📞 {c.phone}</a>
              </div>
            ))}
          </div>

          {/* Multilingual chips */}
          <div className={styles.langs}>
            <span className={styles.langsLabel}>Delivered in {TECH_PARIVARTAN.languages.length} languages:</span>
            {TECH_PARIVARTAN.languages.map((l) => (
              <span key={l} className={styles.langChip}>{l}</span>
            ))}
          </div>

          {/* Mini stats */}
          <div className={styles.parivartanStats}>
            {TECH_PARIVARTAN.stats.slice(0, 4).map((s) => (
              <div key={s.label} className={styles.parivartanStat}>
                <p className={styles.parivartanStatVal}>{s.val}</p>
                <p className={styles.parivartanStatLabel}>{s.label}</p>
              </div>
            ))}
          </div>

          <div className={styles.parivartanCta}>
            <a href="https://sn.nexusaiweb.com/bharat-skilling"
              target="_blank" rel="noopener noreferrer"
              className="btn btn-primary btn-lg">
              Explore Bharat Skilling →
            </a>
          </div>
        </div>
      </section>

      {/* ── OFFLINE CENTERS GLIMPSE (slider + register CTA) ── */}
      <GlimpseSlider />

      {/* ── VERIFIED TESTIMONIALS ── */}
      <section className={styles.testimonials} aria-label="Verified Learner Stories">
        <div className={styles.testimonialsInner}>
          <p className="section-label">Verified Learner Stories</p>
          <h2 className="section-title" style={{ maxWidth: 680, margin: '0 auto 8px' }}>
            Real graduates. <span className="grad">Real companies.</span>
          </h2>
          <p className={styles.testimonialsLead}>
            Reviews sourced directly from LinkedIn &amp; Google by alumni now working at HCL, Infosys, Genpact and across India.
          </p>

          <div className={styles.testimonialsGrid}>
            {TESTIMONIALS.slice(0, 3).map((t) => (
              <article key={t.name} className={styles.testimonialCard}>
                <header className={styles.testimonialHeader}>
                  <div className={styles.stars}>
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <span key={i}>★</span>
                    ))}
                  </div>
                  <span className={`${styles.sourceBadge} ${t.source === 'LinkedIn' ? styles.sourceLinkedIn : styles.sourceGoogle}`}>
                    {t.source} Review
                  </span>
                </header>
                <p className={styles.testimonialQuote}>“{t.text}”</p>
                <footer className={styles.testimonialFooter}>
                  {t.image ? (
                    <img src={t.image} alt={t.name} className={styles.testimonialAvatar} loading="lazy"
                      style={{ objectFit: 'cover' }}
                      onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextSibling.style.display = 'flex'; }} />
                  ) : null}
                  <div className={styles.testimonialAvatar} style={t.image ? { display: 'none' } : undefined}>{t.initials}</div>
                  <div>
                    <p className={styles.testimonialName}>{t.name}</p>
                    <p className={styles.testimonialCompany}>{t.company}</p>
                    <p className={styles.testimonialBatch}>{t.batch} · {t.when}</p>
                  </div>
                </footer>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── ECOSYSTEM ── */}
      <EcosystemSection />

      {/* ── CTA ── */}
      <CtaSection />

      {/* ── MODAL ── */}
      {modal && <Modal type={modal} onClose={closeModal} />}
    </>
  )
}
